import { UserClassModel } from '@/models/class/class';
import { ClassCompetencyContext, ReportGradeDrilldownModel,
         ReportPerformanceCompetencyModel, ReportPerformanceDrilldownModel, ReportPerformanceStatsModel,
        } from '@/models/groups/class';
import { ClassProgressTSModel, GroupStandards } from '@/models/progress-report/progress-report';
import { sortByProperty } from '@/utils/utils';

export class ClassSerializer {

    public static get instance() {
        return this.INSTANCE;
    }

    private static INSTANCE = new ClassSerializer();

    public normalizeClassTimespent(payload: any) {
        const progressData: ClassProgressTSModel[] = [] as ClassProgressTSModel[];
        const reportData = payload.report ? payload.report : [];
        reportData.forEach((item: any) => {
            progressData.push({
                data: this.groupingStandards(item.data),
                reportDate: item.report_date,
            });
        });
        sortByProperty(progressData, 'reportDate', 'ASC');
        return progressData;
    }

    public normalizeClassTimespentAll(payload: any) {
        const reportData = payload.data ? payload.data : [];
        return reportData;
    }

    public normalizeClassCompetencyReport(payload: any) {
        const reportData = payload;
        const listData = {
            masteredList: this.extractMasteryList(reportData.new),
            reinforcedList: this.extractReinforcedList(reportData.reinforced),
            diagnostic: this.extractDiagnosticList(reportData.diagnostic),
            growthList: this.extractGrowthList(reportData.new),
            concernList: this.extractConcernList(reportData.new),
            assessmentInProgress: this.extractAssessmentInProgressList(reportData.new),
        };

        return listData;
    }

    public normalizeClassDashboardReport(payload: any) {
        const report = payload && payload.data && payload.data[0] ? payload.data[0] : {};
        return report;
    }

    public normalizeUserClasses(payload: any) {
        const classPayload: any = payload.classes || [];
        const classList: UserClassModel[] = [];
        classPayload.forEach((item: any) => {
            const classDetails: UserClassModel = {
                id: item.id,
                title: item.title,
                code: item.code,
                courseId: item.course_id,
                subject: item.subject,
                framework: item.framework,
                gradeCurrent: item.grade_current,
                gradeUpperBound: item.grade_upper_bound,
            };
            classList.push(classDetails);
        });
        return classList;
    }


    public normalizeCompetencyGroupsData(payload: any)  {
        const serializedReportsData: ReportPerformanceCompetencyModel = {} as  ReportPerformanceCompetencyModel;
        let activeType = 'country';
        if (payload.context) {
            const reportContext: ClassCompetencyContext = {
                report: payload.context.report,
                hierarchy: payload.context.hierarchy,
                tenants: payload.context.tenants,
                groupId: payload.context.group_id,
                groupType: payload.context.group_type,
                month: payload.context.month,
                year: payload.context.year,
            };
            activeType = payload.context.group_type;
            serializedReportsData.context = reportContext;
        }
        if (payload.data) {
            const performanceData: ReportPerformanceStatsModel[] = [];
            if (activeType !== 'school') {
                payload.data.forEach((payloadItem: any) => {
                    const  payloadDrilldown = payloadItem.drilldown || [];
                    const parsedDrilldown = this.normalizeCompetencyToplevel(payloadDrilldown);
                    sortByProperty(parsedDrilldown, 'drilldown', 'DESC');
                    const reportDrilldown: ReportPerformanceStatsModel = {
                        id: payloadItem.id,
                        type: payloadItem.type,
                        name: payloadItem.name,
                        code: payloadItem.code,
                        drilldown: parsedDrilldown,
                    };
                    performanceData.push(reportDrilldown);
                });
                serializedReportsData.data = performanceData;
            } else {
                serializedReportsData.data = this.normalizeCompetencyToplevel(payload.data || []);
            }
        }
        return serializedReportsData;
    }

    private normalizeCompetencyToplevel(payloadDrilldown: any) {
        return payloadDrilldown.map((topLevel: any) => {
            const gradeList = this.normalizeCompetencyToplevel(topLevel.drilldown || []);
            return {
                averageCompetencyGained: topLevel.average_competency_gained,
                code: topLevel.code,
                gradeId: topLevel.gradeId,
                gradeName: topLevel.gradeName,
                id: topLevel.id,
                name: topLevel.name,
                totalCompetencies: topLevel.total_competencies,
                totalCompetencyGained: topLevel.total_competency_gained,
                totalCompetencyInProgress: topLevel.total_competency_in_progress,
                type: topLevel.type,
                drilldown: gradeList,
            } as ReportPerformanceDrilldownModel;
        });
    }

    private extractReinforcedList(list: any) {
        sortByProperty(list, 'report_date', 'ASC');
        return list;
    }

    private extractDiagnosticList(list: any) {
        sortByProperty(list, 'report_date', 'ASC');
        return list;
    }

    private extractConcernList(list: any) {
        let diagnostic: any = [];
        if (list && list.length > 0) {
            diagnostic = list.filter((item: any) => {
                return item.score < 51 && item.score !== null;
            });
        }
        sortByProperty(diagnostic, 'report_date', 'ASC');
        return diagnostic;
    }

    private extractMasteryList(list: any) {
        let dataList: any = [];
        if (list && list.length > 0) {
            dataList = list.filter((item: any) => {
                return item.score >= 80 && item.status === 'complete';
            });
        }
        sortByProperty(dataList, 'report_date', 'ASC');
        return dataList;
    }

    private extractAssessmentInProgressList(list: any) {
        let dataList: any = [];
        if (list && list.length > 0) {
            dataList = list.filter((item: any) => {
                return item.score === null && item.status === 'inprogress';
            });
        }
        sortByProperty(dataList, 'report_date', 'ASC');
        return dataList;
    }

    private extractGrowthList(list: any) {
        let dataList: any = [];
        if (list && list.length > 0) {
            dataList = list.filter((item: any) => {
                return item.score <= 79 && item.score >= 51 && item.score !== null;
            });
        }
        sortByProperty(dataList, 'report_date', 'ASC');
        return dataList;
    }

    private groupingStandards(payload: any) {
        const results: GroupStandards[] = [];
        if (payload) {
            const competencies = payload.filter(
                (item: any, index: number) => payload.findIndex(
                    (compItem: any) => JSON.stringify(
                        compItem.competencies) === JSON.stringify(item.competencies)) === index);

            competencies.forEach((competency: any) => {
                const compGroup: GroupStandards = {
                    competencies: competency.competencies,
                    collections: payload.filter((list: any) => {
                        if (JSON.stringify(list.competencies) === JSON.stringify(competency.competencies)) {
                            return {
                                format: list.format,
                                id: list.id,
                                score: list.score,
                                sessions: list.sessions,
                                title: list.title,
                                total_timespent: list.total_timespent,
                            };
                        }
                    }),
                };
                results.push(compGroup);
            });
        }
        return results;
    }

}

export const classSerializer = ClassSerializer.instance;
